import type { z } from 'zod';

import { asRecord, mapRecordToBoolean } from '@sites/entity-migrator';

import { legacyDeviceNumber, legacyDeviceValue } from '../../../legacy-device-value';
import type { Device } from '../../../utils';
import { cssKeywordOrNull } from '../../../utils';
import { compactBlockProps, compactDisplayCondition } from '../../compact';

import type { blockDefaultV0Schema } from '../default/v0';
import type { blockSpecificV0Schema } from '../specific/v0';
import type { blockZeroV0Schema } from '../zero/v0';

/**
 * The *input* side of the schemas, not the parsed one: the coercing leaves
 * (`z.coerce.number()`) accept whatever the persisted JSON held.
 */
type BlockDefaultV0Input = z.input<typeof blockDefaultV0Schema>;
type BlockSpecificV0Input = z.input<typeof blockSpecificV0Schema>;
type BlockZeroV0Input = z.input<typeof blockZeroV0Schema>;

type Bag = Record<string, unknown>;

/**
 * Legacy style values are `string | number` — the host wrote paddings as numbers
 * but nothing stopped a `'30'` getting in, so parse rather than type-check.
 */
const num = (value: unknown): number => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
};

const str = (value: unknown): string => (value == null ? '' : String(value));

const bool = (value: unknown): boolean => Boolean(value);

/** A per-side value falls back to nothing here: blocks never had a shorthand. */
const SIDE_KEYS = {
    top: ['borderTopWidth', 'borderTopStyle', 'borderTopColor'],
    right: ['borderRightWidth', 'borderRightStyle', 'borderRightColor'],
    bottom: ['borderBottomWidth', 'borderBottomStyle', 'borderBottomColor'],
    left: ['borderLeftWidth', 'borderLeftStyle', 'borderLeftColor'],
} as const;

type DeviceBags = {
    styles: Bag;
    data: Bag;
    /**
     * The host wrote device bags lazily, and the renderer read an absent one as
     * "inherit desktop". v0 has no cascade, so that inheritance is resolved here.
     */
    hasStyles: boolean;
    hasData: boolean;
};

const deviceBags = (legacy: Bag) => {
    const mobileStyles = asRecord<Bag>(legacy['mobileStyles']);
    const mobileData = asRecord<Bag>(legacy['mobileData']);
    const hasMobileStyles = Boolean(legacy['mobileStyles']);
    const hasMobileData = Boolean(legacy['mobileData']);

    // The tablet bags fall back to the mobile ones, as they always have.
    return {
        desktop: {
            styles: asRecord<Bag>(legacy['styles']),
            data: asRecord<Bag>(legacy['data']),
            hasStyles: true,
            hasData: true,
        },
        tablet: {
            styles: legacy['tabletStyles'] ? asRecord<Bag>(legacy['tabletStyles']) : mobileStyles,
            data: legacy['tabletData'] ? asRecord<Bag>(legacy['tabletData']) : mobileData,
            hasStyles: Boolean(legacy['tabletStyles']) || hasMobileStyles,
            hasData: Boolean(legacy['tabletData']) || hasMobileData,
        },
        mobile: { styles: mobileStyles, data: mobileData, hasStyles: hasMobileStyles, hasData: hasMobileData },
    };
};

type Bags = ReturnType<typeof deviceBags>;

/**
 * Every responsive leaf is emitted for all three breakpoints, so the consumers
 * never have to guess whether an absent key means zero or "same as desktop".
 */
const devices = <T>(bags: Bags, read: (bag: DeviceBags, desktop: DeviceBags, device: Device) => T) => ({
    desktop: read(bags.desktop, bags.desktop, 'desktop'),
    tablet: read(bags.tablet, bags.desktop, 'tablet'),
    mobile: read(bags.mobile, bags.desktop, 'mobile'),
});

/** What a device with no bag of its own reads from. */
const dataBag = (bag: DeviceBags, desktop: DeviceBags): Bag => (bag.hasData ? bag.data : desktop.data);

/** One style key on a device: its own bag, or desktop where the bag is missing or holds the key empty. */
const styleValue = (bag: DeviceBags, desktop: DeviceBags, key: string): unknown =>
    bag.hasStyles ? legacyDeviceValue(bag.styles[key], desktop.styles[key]) : desktop.styles[key];

type SpacingValues = { top: number; right: number; bottom: number; left: number };

/**
 * Top and bottom were written as `${value}px`, so an empty one let desktop show
 * through; left and right as `${value ?? 0}px`, so an absent one is `0`.
 */
const spacing = (styles: Bag, inherited?: SpacingValues) => {
    const vertical = (key: string, fallback = 0) => legacyDeviceNumber(styles[key], fallback);
    const horizontal = (key: string, fallback = 0) =>
        styles[key] == null ? 0 : legacyDeviceNumber(styles[key], fallback);

    return {
        value: {
            top: vertical('paddingTop', inherited?.top),
            right: horizontal('paddingRight', inherited?.right),
            bottom: vertical('paddingBottom', inherited?.bottom),
            left: horizontal('paddingLeft', inherited?.left),
        },
        unit: 'px' as const,
    };
};

type SideValue = { width: number; style: string; color: string };

const side = (styles: Bag, which: keyof typeof SIDE_KEYS, inherited?: SideValue): SideValue => {
    const [widthKey, styleKey, colorKey] = SIDE_KEYS[which];

    return {
        width: legacyDeviceNumber(styles[widthKey], inherited?.width ?? 0),
        style: str(legacyDeviceValue(styles[styleKey], inherited?.style)),
        color: str(legacyDeviceValue(styles[colorKey], inherited?.color)),
    };
};

/**
 * The legacy bag keeps a theme preset name and a literal colour in the same
 * string, and so does v0 (`mixins/color-value`) — the host's `colorCss` tells
 * them apart against its own palette, which this package cannot see. So the
 * string travels verbatim.
 */
const color = (value: unknown): string | null => (value == null ? null : String(value));

/**
 * The picked file travels whole. The first pass at this entity lifted it into a
 * separate integration store and kept only an id here; v0 does not — the file is
 * part of the block, the way it is for the section and the column.
 */
const file = (value: unknown) => (value == null ? null : (value as never));

const shapeDivider = (data: Bag, edge: 'top' | 'bottom') => ({
    isEnabled: bool(data[`is${edge === 'top' ? 'Top' : 'Bottom'}ShapeDivider`]),
    view: str(data[`${edge}ShapeDividerView`]),
    color: str(data[`${edge}ShapeDividerColor`]),
    height: num(data[`${edge}ShapeDividerHeight`]),
    repeat: num(data[`${edge}ShapeDividerRepeat`]),
    horizontalFlip: str(data[`${edge}ShapeDividerHorizontalFlip`]),
    verticalFlip: str(data[`${edge}ShapeDividerVerticalFlip`]),
});

/**
 * Legacy kept the sharing state as a flag plus a `sharedBlockProps` bag; v0
 * writes only the keys that say something, so an ordinary block has none of
 * them. The hidden pages are kept whatever the flag says: a site-wide header
 * stored without it must not come back on the pages it was switched off on.
 */
const sharing = (legacy: Bag): Pick<BlockDefaultV0Input['meta'], 'role' | 'isShared' | 'hiddenOnPages'> => {
    const props = asRecord<Bag>(legacy['sharedBlockProps']);
    const type = props['type'];
    const role = type === 'header' || type === 'footer' ? type : undefined;
    const isShared = bool(legacy['isShared']);
    const hideOnPage = asRecord<Bag>(props['hideOnPage']);
    const hiddenOnPages = Object.keys(hideOnPage).filter((pageId) => bool(hideOnPage[pageId]));

    return {
        ...(role ? { role } : {}),
        ...(isShared ? { isShared: true as const } : {}),
        ...(hiddenOnPages.length ? { hiddenOnPages } : {}),
    };
};

/**
 * The persisted rule, made to pass v0 without changing what the renderer does with it. The old
 * editor did not always write every key, and the renderer has always read a missing `state` as
 * `visible` and an `operator` that is neither `and` nor `or` as "nothing passed" — the block stays
 * hidden; an empty `operator` keeps exactly that. A rule without a condition list has nothing to
 * evaluate (the renderer threw on it) and is dropped.
 */
const displayCondition = (value: unknown) => {
    const rule = asRecord<Bag>(value);
    const conditions = rule['conditions'];

    if (!Array.isArray(conditions)) {
        return null;
    }

    return compactDisplayCondition({
        state: typeof rule['state'] === 'string' ? rule['state'] : 'visible',
        operator: typeof rule['operator'] === 'string' ? rule['operator'] : '',
        conditions: conditions.filter(
            (item): item is Bag => typeof item === 'object' && item !== null && !Array.isArray(item)
        ),
    });
};

const envelope = (legacy: Bag) => ({
    id: str(legacy['id']),
    entityType: 'block' as const,
    children: Array.isArray(legacy['sections']) ? legacy['sections'] : [],
    version: 0 as const,
    // Legacy kept these loose on the block; v0 groups who the block is and when
    // it shows under `meta`.
    meta: {
        displayId: legacy['displayId'] == null ? null : String(legacy['displayId']),
        ...sharing(legacy),
        // Created before the field existed — when is not known.
        createdAt: null,
        // A rule that shows the block to everyone is stored as its absence.
        displayCondition: displayCondition(legacy['blockDisplay']) as never,
    },
});

/** The settings every subtype shares, lifted out of the six legacy bags. */
const commonProps = (legacy: Bag) => {
    const bags = deviceBags(legacy);
    const data = bags.desktop.data;
    // Absent means synchronised — the toggle postdates the blocks that have no
    // key for it, and it shipped on.
    const syncWithDesktop = data['isSynchronizeStyle'] == null ? true : bool(data['isSynchronizeStyle']);

    /**
     * With styles synchronised the renderer ignored the device bags for the
     * contour and the background overlay: the border read
     * `data.isSynchronizeStyle ? data.isBorder : mobileData?.isBorder` (and the
     * desktop border styles with it), and the device tint and filter were
     * recomputed only inside `if (!data.isSynchronizeStyle)`.
     *
     * The device bags are no help there — `mobileData.isBorder` is only written
     * when the toggle flips, and the tint flags sit at their seeded `false` — so
     * reading them per-device drops the border, the tint and the blur below
     * 992px. v0 keeps `syncWithDesktop` as an editor behaviour only (writes
     * propagate), so the render-time short-circuit is resolved here instead.
     */
    const syncedData = (bag: DeviceBags, desktop: DeviceBags): Bag =>
        syncWithDesktop ? desktop.data : dataBag(bag, desktop);

    // Tablet read `tabletData.isBorder ?? mobileData.isBorder`, so a tablet bag
    // saved with the flag empty takes the mobile one.
    const isBorderOn = (bag: DeviceBags, desktop: DeviceBags, device: Device): boolean => {
        const flag = syncedData(bag, desktop)['isBorder'];

        return bool(!syncWithDesktop && device === 'tablet' ? (flag ?? bags.mobile.data['isBorder']) : flag);
    };

    const desktopPadding = spacing(bags.desktop.styles).value;

    const deviceSide = (which: keyof typeof SIDE_KEYS) => {
        const desktopSide = side(bags.desktop.styles, which);

        return devices(bags, (bag, _desktop, device) =>
            device === 'desktop' || syncWithDesktop || !bag.hasStyles
                ? desktopSide
                : side(bag.styles, which, desktopSide)
        );
    };

    return {
        // The two booleans became values: full width is the viewport, full height
        // is a whole screen. `null` on either side is the untouched case — the
        // content column, and a height the contents decide.
        width: bool(data['isFullWidth']) ? { value: 100, unit: 'vw' as const } : null,
        height: {
            desktop: bool(data['isFullHeight']) ? { value: 100, unit: 'vh' as const } : null,
            tablet: bool(data['isFullHeight']) ? { value: 100, unit: 'vh' as const } : null,
            mobile: bool(data['isFullHeight']) ? { value: 100, unit: 'vh' as const } : null,
        },
        // Two switches the panel never let be on together.
        pin: bool(data['pinOnTheTop']) ? ('top' as const) : bool(data['pinOnTheBottom']) ? ('bottom' as const) : null,
        syncWithDesktop,

        hidden: {
            desktop: bool(data['hideOnDesktop']),
            tablet: bool(data['hideOnTablet']),
            mobile: bool(data['hideOnMobile']),
        },

        padding: devices(bags, (bag, _desktop, device) =>
            device !== 'desktop' && bag.hasStyles ? spacing(bag.styles, desktopPadding) : spacing(bags.desktop.styles)
        ),

        // `isIndependentBorders` is dropped rather than carried: the editor wrote
        // all four sides together whenever it was off, so four equal sides say the
        // same thing and `isBorderIndependent` reads it back off them.
        border: {
            isEnabled: devices(bags, isBorderOn),
            top: deviceSide('top'),
            right: deviceSide('right'),
            bottom: deviceSide('bottom'),
            left: deviceSide('left'),
        },

        background: {
            color: devices(bags, (bag, desktop) => color(styleValue(bag, desktop, 'backgroundColor'))),
            image: devices(bags, (bag, desktop) => file(dataBag(bag, desktop)['backgroundImage'])),
            video: devices(bags, (bag, desktop) => file(dataBag(bag, desktop)['backgroundVideo'])),
            size: devices(bags, (bag, desktop) => cssKeywordOrNull(styleValue(bag, desktop, 'backgroundSize'))),
            position: devices(bags, (bag, desktop) => cssKeywordOrNull(styleValue(bag, desktop, 'backgroundPosition'))),
            repeat: devices(bags, (bag, desktop) => cssKeywordOrNull(styleValue(bag, desktop, 'backgroundRepeat'))),
            overlay: {
                isEnabled: devices(bags, (bag, desktop) => bool(syncedData(bag, desktop)['isColorOverflow'])),
                color: devices(bags, (bag, desktop) => str(syncedData(bag, desktop)['colorOverflow'])),
                opacity: devices(bags, (bag, desktop) => num(syncedData(bag, desktop)['colorOverflowOpacity'])),
            },
            filter: {
                isEnabled: devices(bags, (bag, desktop) => bool(syncedData(bag, desktop)['isFilterOverlay'])),
                value: devices(bags, (bag, desktop) => str(syncedData(bag, desktop)['filterOverlay'])),
            },
            scrollEffect: {
                isEnabled: devices(bags, (bag, desktop) => bool(dataBag(bag, desktop)['isScrollEffect'])),
                value: devices(bags, (bag, desktop) => str(dataBag(bag, desktop)['scrollEffect'])),
            },
        },

        shapeDividers: {
            top: devices(bags, (bag, desktop) => shapeDivider(dataBag(bag, desktop), 'top')),
            bottom: devices(bags, (bag, desktop) => shapeDivider(dataBag(bag, desktop), 'bottom')),
        },
    };
};

export const migrateLegacyDefaultToV0 = (input: unknown): BlockDefaultV0Input => {
    const legacy = asRecord<Bag>(input);

    return { ...envelope(legacy), entitySubType: 'default', props: compactBlockProps(commonProps(legacy)) };
};

export const migrateLegacySpecificToV0 = (input: unknown): BlockSpecificV0Input => {
    const legacy = asRecord<Bag>(input);
    const data = asRecord<Bag>(legacy['data']);

    return {
        ...envelope(legacy),
        entitySubType: 'specific',
        props: compactBlockProps({
            ...commonProps(legacy),
            icon: {
                horizontalPosition: str(data['iconHorizontalPosition']),
                size: str(data['iconSize']),
                color: str(data['iconColor']),
            },
            textColor: str(data['textColor']),
            horizontalPosition: str(data['horizontalPosition']),
            hideContentOnMobile: bool(data['hideContentOnMobile']),
            hasSeparator: bool(data['hasSeparator']),
            // Some stored headers hold an empty one as `[]`, which the renderer read as hiding nothing.
            hideWidgetsOnMobile: mapRecordToBoolean(data['hideWidgetsOnMobile']),
        }),
    };
};

export const migrateLegacyZeroToV0 = (input: unknown): BlockZeroV0Input => {
    const legacy = asRecord<Bag>(input);
    const bags = deviceBags(legacy);

    return {
        ...envelope(legacy),
        entitySubType: 'zero',
        props: compactBlockProps({
            ...commonProps(legacy),
            // The canvas is laid out again on a narrow screen, so its height is
            // the one geometry a zero block writes per breakpoint. It overrides
            // the shared `height` above, which a canvas reads as exact rather
            // than as a minimum.
            height: devices(bags, (bag, desktop) => ({
                value: num(dataBag(bag, desktop)['height']),
                unit: 'px' as const,
            })),
            hideOverflow: bool(bags.desktop.data['hideOverflow']),
        }),
    };
};
